/**
 * Traenke als runde Knoepfe in der Kampfecke
 * (Produktentscheidung vom 27.09.2026).
 *
 * Sie haben dieselbe Groesse wie die Passive und sitzen in derselben
 * Daumenecke wie die Faehigkeiten. Frueher standen sie ueber dem Heldenstatus
 * und verschwanden, sobald man keine dabeihatte; beides war unpraktisch: Man
 * sah nicht, dass es ueberhaupt Traenke gibt.
 *
 * Jetzt sind sie immer da. Die Zahl im Kreis sagt, wie viele man hat (0 bis 2),
 * die Farbe sagt, welcher Trank es ist - Leben rot, Mana blau. Ohne Vorrat
 * sind sie ausgegraut und nicht antippbar.
 *
 * Sie liegen UNTER dem Passiv-Ring und der Faehigkeit 1, mittig zwischen
 * deren Aussenkanten - Leben links, Mana rechts (Rueckmeldung vom
 * 27.09.2026). Neben der Faehigkeitengruppe haben sie dem Heldenstatus den
 * Platz weggenommen; unter der Gruppe ist ohnehin freie Flaeche.
 */

import { CONSUMABLES, CONSUMABLE_COOLDOWN_SECONDS } from '@dueline/game-data';
import { t } from '../i18n/index.js';
import type { MatchSnapshot } from '../net/MatchConnection.js';
import { useTap } from './usePress.js';
import { trankBild } from './faehigkeitBilder.js';

interface Props {
  snapshot: MatchSnapshot;
  onUse: (itemId: string) => void;
}

/** Tastenbelegung am Rechner - dieselbe Reihenfolge wie die Consumables. */
const KEY_LABELS: Readonly<Record<string, string>> = {
  health_potion: 'F',
  mana_potion: 'G',
};

export function PotionBar({ snapshot, onUse }: Props) {
  if (!snapshot.heroAlive) return null;

  const vorrat = (id: string) =>
    id === 'health_potion' ? snapshot.healthPotions : snapshot.manaPotions;

  /*
   * Unterwegs (Produktentscheidung vom 05.10.2026).
   *
   * Traenke kommen seit heute erst nach der Lieferzeit an. Ohne diese
   * Anzeige kauft man einen und es passiert scheinbar nichts.
   */
  const unterwegs = (id: string) =>
    id === 'health_potion' ? snapshot.healthPotionsIncoming : snapshot.manaPotionsIncoming;
  const ankunft = (id: string) =>
    id === 'health_potion' ? snapshot.healthPotionNextSeconds : snapshot.manaPotionNextSeconds;

  const abklingend = snapshot.potionCooldown > 0;
  // Anteil der noch laufenden Abklingzeit - fuellt den Kreis von unten.
  const anteil = abklingend
    ? Math.min(1, snapshot.potionCooldown / CONSUMABLE_COOLDOWN_SECONDS)
    : 0;

  return (
    <div className="dl-potionbar">
      {CONSUMABLES.map((consumable) => (
        <PotionButton
          key={consumable.id}
          consumable={consumable}
          anzahl={vorrat(consumable.id)}
          unterwegs={unterwegs(consumable.id)}
          ankunftSekunden={ankunft(consumable.id)}
          abklingend={abklingend}
          anteil={anteil}
          restSekunden={snapshot.potionCooldown}
          onUse={onUse}
        />
      ))}
    </div>
  );
}

/**
 * Ein einzelner Trank.
 *
 * Eigene Komponente, weil useTap ein Haken ist - Haken duerfen nicht in einer
 * Schleife stehen.
 */
function PotionButton({
  consumable,
  anzahl,
  unterwegs,
  ankunftSekunden,
  abklingend,
  anteil,
  restSekunden,
  onUse,
}: {
  consumable: (typeof CONSUMABLES)[number];
  anzahl: number;
  /** Wie viele dieser Art gerade geliefert werden. */
  unterwegs: number;
  /** Restzeit des naechsten. 0 = keiner unterwegs. */
  ankunftSekunden: number;
  abklingend: boolean;
  anteil: number;
  restSekunden: number;
  onUse: (itemId: string) => void;
}) {
  const leer = anzahl <= 0;
  const taste = KEY_LABELS[consumable.id] ?? '';
  // Trinken muss auch beim Laufen gehen - deshalb pointerup statt click.
  const press = useTap(() => onUse(consumable.id));
  const bild = trankBild(consumable.id);
  const abklingFuellung = (
    <span
      className="dl-potion__cooldown"
      style={{ height: `${anteil * 100}%` }}
      aria-hidden="true"
    />
  );

  return (
    <button
      type="button"
      className={
        `dl-potion dl-potion--${consumable.restores}` +
        (leer ? ' dl-potion--empty' : '') +
        (abklingend && !leer ? ' dl-potion--cooling' : '') +
        (bild ? ' dl-potion--bild' : '')
      }
      disabled={leer || abklingend}
      title={`${consumable.name} (${taste}) - ${t.shop.useHint}`}
      aria-label={
        unterwegs > 0
          ? `${consumable.name}: ${anzahl}, ${unterwegs} ${t.shop.incoming}`
          : `${consumable.name}: ${anzahl}`
      }
      {...press}
    >
      {/*
        Mit Bild (16.10.2026): Flasche und Abklingfuellung liegen in einer
        runden Huelle, die Zahl wird ein Schild auf der Kante - so bleibt die
        Flasche ganz zu sehen.
      */}
      {bild ? (
        <span className="dl-potion__rund" aria-hidden="true">
          <picture className="dl-potion__bild">
            <source type="image/avif" srcSet={bild.avif} sizes="64px" />
            <source type="image/webp" srcSet={bild.webp} sizes="64px" />
            <img src={bild.ersatz} alt="" decoding="async" />
          </picture>
          {abklingFuellung}
        </span>
      ) : (
        abklingFuellung
      )}
      <span className="dl-potion__count">{anzahl}</span>
      {/* Unterwegs: die Sekunden bis zum naechsten. Sonst saehe der Kauf
          aus, als waere nichts passiert. */}
      {unterwegs > 0 && <span className="dl-potion__incoming">+{Math.ceil(ankunftSekunden)}s</span>}
      {/* Waehrend der Abklingzeit sagt die zweite Zahl, wie lange noch - die
          Fuellung allein waere nur Farbe (Masterprompt Abschnitt 40). */}
      {abklingend && !leer && <span className="dl-potion__timer">{Math.ceil(restSekunden)}s</span>}
    </button>
  );
}
